<template>
  <form
    class="donor-form-card"
    method="POST"
    target="_blank"
    @submit.prevent="submitPaymentDataForm"
  >
    <div class="donor-form-card__body">
      <div class="donor-form-header">
        <div class="donor-form-header__icon">
          <CreditCardIcon class="h-5 w-5 text-primaryAccent" aria-hidden="true" />
        </div>
        <div class="donor-form-header__text">
          <h2 class="donor-form-title">Payment Details</h2>
          <p class="donor-form-subtitle">Choose your donation amount and complete payment.</p>
        </div>
      </div>

      <section class="donor-form-section">
        <p class="donor-form-section-title">Frequency</p>
        <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div v-for="option in uniqueFrequencyOptions" :key="option.name">
            <input
              :id="option.name"
              name="frequency"
              type="radio"
              :value="option"
              :errors="form.levelAutorepeat.errors"
              @blur="form.levelAutorepeat.validate()"
              class="peer sr-only"
              v-model="form.levelAutorepeat.value"
              :checked="option.id === form.levelAutorepeat.value?.id"
              @input="clearCustomAmount"
              @change="handleFrequencyChange(option)"
            />
            <label :for="option.name" class="donor-form-choice">{{ option.name }}</label>
          </div>
        </div>
        <p
          v-if="isYearlySelected && paymentProvider === 'SpringBoard'"
          class="text-xs font-semibold italic text-gray-700 sm:text-sm"
        >
          ***All annual donations are matched in the first year!***
        </p>
      </section>

      <section
        v-if="form.levelAutorepeat.value"
        class="donor-form-section donor-form-section-divider"
      >
        <p class="donor-form-section-title">Donation amount</p>
        <template v-for="option in uniqueFrequencyOptions" :key="option.name + '-amount'">
          <fieldset
            v-if="option.id === form.levelAutorepeat.value.id"
            :id="option.name + '-amount'"
            class="donation-amount-group"
          >
            <div class="flex flex-wrap items-center gap-3">
              <template
                v-for="(amount, index) in sortedSuggestedAmounts(option.suggestedAmounts)"
                :key="amount"
              >
                <span class="inline-flex">
                  <input
                    :id="'amount' + amount + '-' + index"
                    :name="option.value"
                    type="radio"
                    :value="amount"
                    class="peer sr-only"
                    v-model="form.donationAmount.value"
                    :checked="amount === form.donationAmount.value"
                    @input="clearCustomAmount"
                  />
                  <label
                    class="donation-amount-preset cursor-pointer peer-checked:border-primary peer-checked:bg-primary peer-checked:text-white"
                    :for="'amount' + amount + '-' + index"
                  >
                    ${{ amount }}
                  </label>
                </span>
              </template>

              <div
                class="donation-amount-custom"
                :class="{
                  'donation-amount-custom--active':
                    isCustomAmountFocused || customAmountInput !== '',
                }"
              >
                <span class="donation-amount-custom-prefix">$</span>
                <input
                  id="custom-amount-input"
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  autocomplete="off"
                  class="donation-amount-custom-input"
                  placeholder="0"
                  :size="customAmountInputSize"
                  :value="customAmountInput"
                  @input="onCustomAmountInputEvent"
                  @focus="onCustomAmountFocus"
                  @blur="
                    () => {
                      onCustomAmountBlur()
                      checkCustomAmount()
                    }
                  "
                />
                <span class="donation-amount-custom-suffix">.00</span>
                <span class="donation-amount-custom-label">Other</span>
              </div>
            </div>
            <ul v-if="customAmountErrors.length" class="input--errors mt-1">
              <li
                v-for="(error, index) in customAmountErrors"
                :key="index"
                v-text="error.message"
                class="input--error"
              />
            </ul>
          </fieldset>
        </template>
      </section>

      <section
        v-if="canShowSMSButton && qrCode == 'false'"
        class="donor-form-section donor-form-section-divider"
      >
        <div class="flex justify-start">
          <BaseButton
            type="button"
            :disabled="smsSent || smsLoading"
            @click="smsForDonationVerification"
          >
            <span v-if="smsLoading">Sending...</span>
            <span v-else-if="smsSent">SMS Sent</span>
            <span v-else>Send Verification SMS</span>
          </BaseButton>
        </div>
        <p v-if="smsError" class="input--error mt-2">{{ smsError }}</p>
        <p v-if="smsSent && !smsError" class="mt-2 text-sm text-green-600">
          Verification SMS sent successfully!
        </p>
      </section>

      <section class="donor-form-section donor-form-section-divider">
        <p class="donor-form-section-title">
          {{ tokenizesCardOffForm ? 'Billing details' : 'Card details' }}
        </p>
        <div class="donor-form-grid">
          <input
            type="text"
            text="billing-name-first"
            id="billing-name-first"
            class="input--text--small"
            placeholder="First name"
            v-model="form.billingNameFirst.value"
            @input="onBillingNameInput($event, 'billingNameFirst')"
          />
          <input
            type="text"
            text="billing-name-last"
            id="billing-name-last"
            class="input--text--small"
            placeholder="Last name"
            v-model="form.billingNameLast.value"
            @input="onBillingNameInput($event, 'billingNameLast')"
            @blur="evaluateBillingLastNameMismatchOnBlur"
          />
          <template v-if="!tokenizesCardOffForm">
            <div class="donor-form-field-full donor-form-grid-thirds">
              <div class="relative min-w-0">
                <label for="card-number" class="sr-only">Card number</label>
                <input
                  inputmode="tel"
                  text="card-number"
                  id="card-number"
                  class="input--text--small"
                  placeholder="Card number"
                  autocomplete="off"
                  v-model="form.cardNumber.value"
                  v-cardformat:formatCardNumber
                  @blur="maskCreditCard"
                />
                <span v-if="form.cardNumber.errors?.length" class="mt-1 text-xs text-red-600">
                  {{ form.cardNumber.errors[0].message }}
                </span>

                <input
                  class="input--text--small absolute left-0 top-0"
                  v-model="hiddenCreditCard"
                  autocomplete="off"
                  @click="unmaskCreditCard"
                  v-if="maskCCVisible"
                />
              </div>
              <div class="min-w-0">
                <label for="card-expiration-date" class="sr-only">Expiration date</label>
                <InputField
                  label-size="small"
                  input-text-size="small"
                  v-model:value="form.cardExpDate.value"
                  v-cardformat:formatCardExpiry
                  :errors="form.cardExpDate.errors"
                  @blur="form.cardExpDate.validate()"
                  inputmode="tel"
                  placeholder="MM / YYYY"
                  autocomplete="off"
                />
              </div>
              <div class="min-w-0">
                <label for="card-cvc" class="sr-only">CVC</label>
                <input
                  inputmode="tel"
                  text="card-cvc"
                  id="card-cvc"
                  class="input--text--small w-full"
                  placeholder="CVC"
                  autocomplete="off"
                  v-model="form.cardCvv.value"
                  v-cardformat:formatCardCVC
                />
              </div>
            </div>
          </template>
        </div>
        <p
          v-if="showBillingLastNameMismatchWarning"
          class="text-sm italic leading-5 text-orange-600"
        >
          Alert: This name does not match the name that was entered on page 1 for personal details.
          Before submitting, please confirm all information is accurate.
        </p>
        <p v-if="paymentProvider === 'BBMSCheckout'" class="text-sm italic text-gray-500">
          Card details will be collected securely via Blackbaud Checkout after you click Submit.
        </p>
      </section>

      <section v-if="isInlineCheckout" class="donor-form-section donor-form-section-divider">
        <p class="donor-form-section-title">Card details</p>
        <div id="bb-inline-checkout" class="min-h-[6rem]"></div>
        <p v-if="!inlineCheckoutReady" class="text-sm italic text-gray-500">
          Loading secure payment fields…
        </p>
        <p v-if="inlineCheckoutError" class="input--error mt-2">{{ inlineCheckoutError }}</p>
      </section>

      <section class="donor-form-section donor-form-section-divider">
        <p class="donor-form-section-title">Billing address</p>

        <label class="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            id="same-address"
            text="same-address"
            type="checkbox"
            class="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            @change="autoSetAddress = !autoSetAddress"
            :checked="autoSetAddress"
          />
          Use same as contact
        </label>

        <div class="donor-form-address">
          <div class="donor-form-grid-thirds">
            <PlacesStreetAddressInput
              label-size="small"
              input-text-size="small"
              v-model:value="form.billingAddressStreet1.value"
              :errors="form.billingAddressStreet1.errors"
              label="Street address"
              type="text"
              id="billing-street-address"
              placeholder="Start typing your address..."
              :readonly="billingAddressAutofilled && autoSetAddress"
              @place-selected="onBillingAddressPlaceSelected"
              @invalid-selection="onBillingAddressInvalidSelection"
              @blur="handleBillingAddressBlur"
              @input="handleBillingAddressInput"
            />
            <InputField
              label-size="small"
              input-text-size="small"
              v-model:value="form.billingAddressStreet2.value"
              :errors="form.billingAddressStreet2.errors"
              @blur="form.billingAddressStreet2.validate()"
              label="Apt, Floor, or Suite"
              type="text"
              text="address-detail"
              id="billing-address-detail"
              placeholder="Optional"
            />
            <InputField
              label-size="small"
              input-text-size="small"
              v-model:value="form.donorEmail.value"
              :errors="form.donorEmail.errors"
              @blur="form.donorEmail.validate()"
              label="Email"
              type="email"
              text="email"
              id="donor-email"
              placeholder="example@email.com"
              autocomplete="email"
            />
          </div>
          <div class="donor-form-grid-thirds">
            <InputField
              label-size="small"
              input-text-size="small"
              v-model:value="form.billingAddressCity.value"
              :errors="form.billingAddressCity.errors"
              @blur="form.billingAddressCity.validate()"
              label="City"
              type="text"
              text="city"
              id="billing-city"
              placeholder="City"
              autocomplete="address-level2"
              :readonly="billingAddressAutofilled"
            />
            <SelectField
              small
              :select-options="usStateData"
              v-model:value="form.billingAddressState.value"
              label="State"
              :errors="form.billingAddressState.errors"
              :disabled="billingAddressAutofilled"
            />
            <InputField
              label-size="small"
              input-text-size="small"
              v-model:value="form.billingAddressZip.value"
              :errors="form.billingAddressZip.errors"
              @blur="form.billingAddressZip.validate()"
              label="Zip / Postal code"
              type="text"
              text="postal-code"
              id="billing-postal-code"
              placeholder="ZIP"
              autocomplete="postal-code"
              :readonly="billingAddressAutofilled"
            />
          </div>
        </div>
      </section>

      <section class="donor-form-section donor-form-section-divider">
        <div class="flex items-start gap-3">
          <input
            id="terms"
            text="terms"
            type="checkbox"
            class="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
            v-model="form.agreedToTerms.value"
          />
          <template v-if="paymentProvider === 'EngagingNetwork'">
            <label for="terms" class="text-xs leading-relaxed text-gray-700">
              By submitting this form, I understand that I am agreeing to make recurring charitable
              donations to World Wildlife Fund (WWF) in the amount and at the frequency selected
              above, and I authorize my credit/bank to make those payments from the indicate
              account. I acknowledge that I will not receive any goods/services in exchange for my
              donation. I will receive an acknowledgment of my initial payment via the email address
              I provided. Records of subsequent recurring payment(s) will appear on my credit card
              or bank statement.
              <br />
              <br />
              I acknowledge that my donations will be used in general support of WWF’s conservation
              mission and that I may cancel future recurring donations at any time by calling WWF at
              1-833-993-4376. I further understand that the individual(s) who solicited my donations
              to WWF are paid fundraisers employed by New Canvassing Experience, an agency hired by
              WWF to conduct face-to-face fundraising, which receives a fee for recruiting new WWF
              supporters.
              <br />
              <br />
              I understand that in submitting this form, I am providing certain personal information
              to WWF. WWF’s use of my information is subject to WWF’s privacy policy, available at
              https://www.worldwildlife.org/pages/privacy- policy. I agree to allow WWF and its
              authorized representatives (including NCE) to contact me by telephone, text, email, or
              mail, including regarding my donation. If I agree to take a picture with my fundraiser
              today, I give consent to World Wildlife Fund and its licensees to use my image, agree
              that I am not owed any compensation for use of my image, and release WWF from all
              claims relating to use of my image.
              <br />
              <br />
              I certify that I am at least 25 years old and make the recurring charitable donations
              described in this form of my own free will.
            </label>
          </template>
          <template v-else-if="paymentProvider === 'NAACPCustomAPI'">
            <label for="terms" class="text-xs leading-relaxed text-gray-700">
              I give consent to NAACP having ownership rights to my photo or image, to reproduce and
              use for any purpose worldwide in furtherance of the NAACP’s mission without exception
              of compensation to me now or in the future.
            </label>
          </template>
          <template v-else>
            <label for="terms" class="text-xs leading-relaxed text-gray-700">
              I understand that I have spoken to a paid fundraiser employed by New Canvassing
              Experience, Inc., an agency paid a fee by {{ npoName }} to conduct this face-to-face
              fundraising campaign. I authorize the above amount to be donated to the
              {{ npoName }} from my credit card at the frequency selected until further notice. I
              also allow New Canvassing Experience and its partnered agencies to contact me
              regarding my donation via the contact information entered on this form. A record of
              each payment will appear on my credit card statement and will serve as a receipt for
              any subsequent donations.
            </label>
          </template>
        </div>
      </section>

      <DonorFormCustomQuestions
        @questionData="getQuestionData"
        v-bind:campaign-api-url="campaignApiUrl"
      />
    </div>

    <div class="donor-form-actions">
      <BaseButton type="button" variant="secondary" @click="prevStep">Back</BaseButton>
      <!-- Blackbaud's inline workflow owns the submit button: it attaches its own click
           listener to the wrapper and starts authorization from there, so the button must
           not also submit our form. -->
      <div v-if="isInlineCheckout" id="bb-complete-checkout-button">
        <BaseButton
          id="bb-form-submit-button"
          name="bb-form-submit-button"
          type="button"
          :loading="inlineCheckoutBusy"
        >
          Submit Donation
        </BaseButton>
      </div>
      <BaseButton v-else id="donor-data-submit" type="submit">Submit Donation</BaseButton>
    </div>
  </form>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch, camelize } from 'vue'
import { CreditCardIcon } from '@heroicons/vue/20/solid'
import SelectField from '@/components/inputs/SelectField'
import InputField from '@/components/inputs/InputField'
import PlacesStreetAddressInput from '@/components/inputs/PlacesStreetAddressInput.vue'
import { PaymentDonorForm, ContactDonorForm } from '@/services/users/'
import DonorFormCustomQuestions from '@/components/DonorFormCustomQuestions.vue'
import usStateData from '@/data/usStateData.js'
import { sanitizeName } from '@/utils/misc'
import {
  clampCustomAmountDigits,
  parseCustomAmountNumber,
  resolveDonationAmount,
  sanitizeCustomAmountInput,
  sortedSuggestedAmounts,
} from '@/utils/donationAmountInput'
import { UserApi } from '@/services/users/'
import { DonationApi } from '../services/donations/api'
import { createInlineCheckout } from '@/services/donor/bbmsCheckoutInline'
import { loadRecaptcha } from '@/services/donor/recaptcha'
import { ONETIME } from '@/services/frequencies/options'
import { readStoredFormToken, requestDonorFormToken } from '@/services/formAccess'

export default {
  name: 'DonorFormPayment',
  components: {
    SelectField,
    InputField,
    PlacesStreetAddressInput,
    DonorFormCustomQuestions,
    CreditCardIcon,
  },

  props: {
    frequencyOptions: {
      type: Array,
      default: () => [],
    },
    campaignApiUrl: {
      type: String,
      default: '',
    },
    npoName: {
      type: String,
      default: '',
      required: true,
    },
    emailOptIn: {
      type: Boolean,
      default: true,
      required: true,
    },
    paymentProviders: {
      type: String,
      default: '',
      required: true,
    },
    mobilePhone: {
      type: String,
      default: '',
      required: true,
    },
    email: {
      type: String,
      default: '',
      required: true,
    },
    isPaymentStepVisible: {
      type: Boolean,
      default: false,
    },
    backStep: {
      type: Number,
      default: 1,
    },
    donorVerificationEnabled: {
      type: Boolean,
      default: true,
    },
    paymentPrefill: {
      type: Object,
      default: null,
    },
    preserveUniqueId: {
      type: Boolean,
      default: false,
    },
    // Blackbaud Checkout settings from the campaign's PaymentProvider:
    // { environmentId, paymentConfigurationId, checkoutZone, recaptchaSiteKey }.
    checkoutConfig: {
      type: Object,
      default: null,
    },
  },
  emits: ['donorData', 'stepNumber', 'submitForm', 'checkoutStarted', 'checkoutError'],

  setup(props, { emit }) {
    const isCustomAmountFocused = ref(false)
    const customAmountInput = ref('')
    const form = ref(new PaymentDonorForm())
    const autoSetAddress = ref(true)

    function syncCustomAmountToForm(raw = customAmountInput.value) {
      const clamped = clampCustomAmountDigits(sanitizeCustomAmountInput(raw))
      customAmountInput.value = clamped
      form.value.customAmount.value = clamped
      return clamped
    }

    const applyPaymentPrefill = (values) => {
      if (!values || !Object.keys(values).length) return
      autoSetAddress.value = !values.cardNumber
      form.value = PaymentDonorForm.create({ ...values })
      customAmountInput.value =
        values.customAmount != null && values.customAmount !== '' ? String(values.customAmount) : ''
    }

    watch(() => props.paymentPrefill, applyPaymentPrefill, { immediate: true, deep: true })
    const donateSuccess = ref(false)
    const amountRadioButtons = ref(null)
    const frequency = ref('')
    const selectedFrequency = ref([])
    const amount = ref('')
    const selectedAmount = ref('')
    const minCustomAmount = ref(0)
    const terms = ref(false)
    const questionData = ref([])
    const maskCCVisible = ref(false)
    const maskCCLength = ref(0)
    const hiddenCreditCard = ref('')
    const customAmountErrors = ref([])
    const cardBrand = ref('')
    const paymentProvider = computed(() => props.paymentProviders)
    const isInlineCheckout = computed(() => paymentProvider.value === 'BBMSCheckoutInline')
    // Both Blackbaud Checkout workflows tokenize the card inside Blackbaud's own iframe,
    // so our card fields stay out of the form entirely.
    const tokenizesCardOffForm = computed(
      () => paymentProvider.value === 'BBMSCheckout' || isInlineCheckout.value,
    )
    const inlineCheckout = ref(null)
    const inlineCheckoutReady = ref(false)
    const inlineCheckoutValid = ref(false)
    const inlineCheckoutBusy = ref(false)
    const inlineCheckoutError = ref('')
    const smsSent = ref(false)
    const smsError = ref('')
    const smsLoading = ref(false)
    const qrCode = ref(false)

    const normalizeName = (name = '') => name.trim().replace(/\s+/g, ' ').toLowerCase()
    const getPersonalDetailsLastName = () => {
      const donorData = JSON.parse(localStorage.getItem('DonorData')) || {}
      return donorData?.donor?.lastName || donorData?.lastName || ''
    }
    const showBillingLastNameMismatchWarning = ref(false)

    function evaluateBillingLastNameMismatchOnBlur() {
      const billingLastName = normalizeName(form.value.billingNameLast.value || '')
      const personalLastName = normalizeName(getPersonalDetailsLastName())
      if (!billingLastName || !personalLastName) {
        showBillingLastNameMismatchWarning.value = false
        return
      }
      showBillingLastNameMismatchWarning.value = billingLastName !== personalLastName
    }

    // Strip digits and special characters from billing name fields the moment they're
    // typed. We rewrite event.target.value directly so the invalid character disappears
    // in the same event tick, then sync the cleaned value back into form state.
    function onBillingNameInput(event, fieldName) {
      const sanitized = sanitizeName(event.target.value)
      if (event.target.value !== sanitized) {
        event.target.value = sanitized
      }
      form.value[fieldName].value = sanitized
    }

    const billingAddressAutofilled = ref(false)

    const customAmountInputSize = computed(() =>
      Math.max(2, String(customAmountInput.value || '').length || 1),
    )

    // Computed property for unique frequency options
    const uniqueFrequencyOptions = computed(() => {
      const frequencyMap = new Map()

      props.frequencyOptions.forEach((option) => {
        // Use 'value' property to identify unique frequencies
        // Change to 'id' if you prefer uniqueness by ID
        if (option.active && !frequencyMap.has(option.value)) {
          frequencyMap.set(option.value, option)
        }
      })

      return Array.from(frequencyMap.values())
    })

    const generateUUID = () =>
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
      })

    onMounted(() => {
      if (!props.paymentPrefill?.uniqueId) {
        form.value.uniqueId.value = generateUUID()
      }
    })

    function onBillingAddressPlaceSelected(parsed) {
      form.value.billingAddressCity.value = parsed.city
      form.value.billingAddressState.value = parsed.state
      form.value.billingAddressZip.value = parsed.postalCode
      billingAddressAutofilled.value = true
      form.value.billingAddressStreet1.errors = []
    }

    function onBillingAddressInvalidSelection() {
      form.value.billingAddressStreet1.errors = [
        { message: 'Please select a valid address from the dropdown' },
      ]
    }

    function handleBillingAddressBlur() {
      form.value.billingAddressStreet1.validate()
      if (!billingAddressAutofilled.value && form.value.billingAddressStreet1.value) {
        form.value.billingAddressStreet1.value = ''
      }
    }

    function handleBillingAddressInput() {
      // If user starts typing manually, reset the autofilled flag
      if (billingAddressAutofilled.value) {
        billingAddressAutofilled.value = false
        form.value.billingAddressStreet1.value = ''
        form.value.billingAddressCity.value = ''
        form.value.billingAddressState.value = ''
        form.value.billingAddressZip.value = ''
      }
    }

    const billingFieldsFromContact = [
      'billingAddressStreet1',
      'billingAddressStreet2',
      'billingAddressCity',
      'billingAddressState',
      'billingAddressZip',
      'donorEmail',
    ]

    /** Update billing fields in place — never recreate the form (preserves custom amount). */
    function applyContactBillingToForm(donorData) {
      const contactData = ContactDonorForm.create(donorData ?? {}).toBillingAddressVal
      for (const key of billingFieldsFromContact) {
        if (form.value[key]) {
          form.value[key].value = contactData[key] ?? ''
        }
      }
    }

    function clearBillingFields() {
      for (const key of billingFieldsFromContact) {
        if (form.value[key]) {
          form.value[key].value = ''
        }
      }
    }

    function syncBillingFromContact() {
      if (!autoSetAddress.value) return
      const donorData = JSON.parse(localStorage.getItem('DonorData'))?.donor ?? {}
      applyContactBillingToForm(donorData)
      billingAddressAutofilled.value = true
    }

    watch(
      () => props.emailOptIn,
      (emailOptIn) => {
        if (props.paymentPrefill && Object.keys(props.paymentPrefill).length) return
        form.value.donorEmailOptIn.value = emailOptIn
      },
      { immediate: true },
    )

    watch(customAmountInput, (customAmount) => {
      if (customAmount) {
        form.value.donationAmount.value = null
      }
    })

    watch(
      autoSetAddress,
      (useContactAddress) => {
        if (props.paymentPrefill && Object.keys(props.paymentPrefill).length) return

        if (useContactAddress) {
          const donorData = JSON.parse(localStorage.getItem('DonorData'))?.donor ?? {}
          applyContactBillingToForm(donorData)
          billingAddressAutofilled.value = true
        } else {
          clearBillingFields()
          billingAddressAutofilled.value = false
        }
      },
      { immediate: true },
    )

    watch(
      () => props.isPaymentStepVisible,
      (visible) => {
        if (!visible) {
          showBillingLastNameMismatchWarning.value = false
        }
        if (visible && autoSetAddress.value) {
          nextTick(syncBillingFromContact)
        }
      },
    )

    watch(
      () => props.donorVerificationEnabled,
      (enabled) => {
        if (!enabled) {
          form.value.donorVerificationSms.value = false
          smsSent.value = false
          smsError.value = ''
        }
      },
      { immediate: true },
    )

    function checkCustomAmount() {
      customAmountErrors.value = [] // Reset errors on each check

      const freq = form.value.levelAutorepeat.value
      if (!freq) {
        return
      }

      const amount = parseCustomAmountNumber(customAmountInput.value)
      if (!amount) return

      if (amount < freq.minimum) {
        customAmountErrors.value.push({
          code: 'invalidMinValue',
          isRequired: true,
          message: `Minimum ${freq.name?.toLowerCase() || 'donation'} donation is $${
            freq.minimum
          }.`,
          min: freq.minimum,
        })
      } else if (freq.maximum && amount > freq.maximum) {
        customAmountErrors.value.push({
          code: 'invalidMaxValue',
          isRequired: true,
          message: `Please enter a value that is $${freq.maximum} or less.`,
          max: freq.maximum,
        })
      } else if (String(customAmountInput.value).includes('.')) {
        customAmountErrors.value.push({
          code: 'invalidDecimalValue',
          isRequired: true,
          message: 'Decimal value not allowed.',
        })
      }
    }

    function clearCustomAmount() {
      customAmountInput.value = ''
      form.value.customAmount.value = ''
      customAmountErrors.value = []
    }

    function maskCreditCard() {
      if (form.value.cardNumber.value) {
        maskCCVisible.value = true
        maskCCLength.value = form.value.cardNumber.value.length
        hiddenCreditCard.value = '•'.repeat(maskCCLength.value)
      }
    }

    function unmaskCreditCard() {
      maskCCVisible.value = false

      const ccField = document.getElementById('card-number')
      ccField.focus()
    }

    function prevStep() {
      emit('stepNumber', props.backStep ?? 1)
    }

    function stripCardValidators(formInstance) {
      formInstance.cardNumber.validators = []
      formInstance.cardCvv.validators = []
      formInstance.cardExpDate.validators = []
    }

    function validatePaymentForm() {
      const unwrappedForm = form.value
      // Trim all string values in the form
      Object.keys(unwrappedForm).forEach((key) => {
        if (unwrappedForm[key]?.value && typeof unwrappedForm[key].value === 'string') {
          unwrappedForm[key].value = unwrappedForm[key].value.trim().replace(/\s+/g, ' ')
        }
      })

      if (tokenizesCardOffForm.value) {
        stripCardValidators(unwrappedForm)
      }

      unwrappedForm.validate()

      // Normalize custom amount once more before resolving (mobile paste/autofill).
      syncCustomAmountToForm()

      const freq = unwrappedForm.levelAutorepeat.value
      const hasCustomAmount = Boolean(customAmountInput.value)
      const hasPresetAmount =
        unwrappedForm.donationAmount.value != null && unwrappedForm.donationAmount.value !== ''

      // Only fall back to default when the donor did not enter or choose an amount.
      if (freq && !hasCustomAmount && !hasPresetAmount && freq.defaultAmount != null) {
        unwrappedForm.donationAmount.value = freq.defaultAmount
      }

      const finalAmount = resolveDonationAmount({
        customAmount: customAmountInput.value,
        donationAmount: unwrappedForm.donationAmount.value,
        defaultAmount: null,
      })

      if (freq && !finalAmount) {
        customAmountErrors.value = [
          {
            code: 'invalidAmount',
            isRequired: true,
            message: 'Please select or enter a donation amount.',
          },
        ]
        return false
      }
      if (freq?.maximum && finalAmount > freq.maximum) {
        customAmountErrors.value = [
          {
            code: 'invalidMaxValue',
            isRequired: true,
            message: `Please enter a value that is $${freq.maximum} or less.`,
            max: freq.maximum,
          },
        ]
        return false
      }

      return unwrappedForm.isValid && !customAmountErrors.value.length
    }

    function buildSubmitPayload(extra = {}) {
      return {
        ...form.value.formVal,
        questionData: questionData.value,
        cardBrand: cardBrand.value,
        ...extra,
      }
    }

    function submitPaymentDataForm() {
      if (!validatePaymentForm()) return
      emit('submitForm', buildSubmitPayload())
      form.value.uniqueId.value = generateUUID()
    }

    const onCustomAmountInputEvent = (event) => {
      const sanitized = sanitizeCustomAmountInput(event.target.value)
      const clamped = clampCustomAmountDigits(sanitized)
      customAmountInput.value = clamped
      form.value.customAmount.value = clamped
      if (clamped) {
        form.value.donationAmount.value = null
      }
      if (sanitized.length > 5) {
        customAmountErrors.value = [
          { code: 'invalidMaxDigits', isRequired: true, message: 'Please enter 5 digits or less.' },
        ]
      } else {
        checkCustomAmount()
      }
    }

    const onCustomAmountFocus = () => {
      isCustomAmountFocused.value = true
      const unwrappedForm = form.value
      unwrappedForm.donationAmount.value = ''
    }

    const onCustomAmountBlur = () => {
      isCustomAmountFocused.value = false
    }

    function handleFrequencyChange(newFrequency) {
      if (!props.preserveUniqueId) {
        form.value.uniqueId.value = generateUUID()
      }
      form.value.donorVerificationSms.value = false
      qrCode.value = localStorage.getItem('qrCode')
      // Clear the donation amount when frequency changes
      form.value.donationAmount.value = null
      clearCustomAmount()

      // Set the new frequency
      form.value.levelAutorepeat.value = newFrequency
    }
    const isYearlySelected = computed(() => {
      return (
        props.frequencyOptions &&
        props.frequencyOptions.length > 0 &&
        props.frequencyOptions[0]?.value === 'annually'
      )
    })
    const checkBin = async (digits) => {
      try {
        const result = await UserApi.csc.checkBinAvailability({ bin: digits })
        return result.allowed
      } catch (error) {
        console.error('Error checking BIN:', error)
      }
    }

    const canShowSMSButton = computed(() => {
      if (!props.donorVerificationEnabled) return false
      const amount = form.value.donationAmount.value || customAmountInput.value
      return !!amount
    })

    const smsForDonationVerification = async () => {
      const donorData = JSON.parse(localStorage.getItem('DonorData')) || {}
      smsError.value = ''
      smsLoading.value = true

      const freqOption = form.value.levelAutorepeat.value
      if (!freqOption) {
        smsError.value = 'Please select a donation frequency first.'
        smsLoading.value = false
        return
      }

      const customAmount = syncCustomAmountToForm()

      // Check for custom amount validity
      if (customAmount) {
        const numericAmount = parseCustomAmountNumber(customAmount)
        if (numericAmount < freqOption.minimum) {
          smsError.value = `Minimum ${freqOption.name?.toLowerCase() || 'donation'} donation is $${
            freqOption.minimum
          }.`
          smsLoading.value = false
          return
        }
        if (freqOption.maximum && numericAmount > freqOption.maximum) {
          smsError.value = `Please enter a value that is $${freqOption.maximum} or less.`
          smsLoading.value = false
          return
        }
        if (String(customAmount).includes('.')) {
          smsError.value = 'Decimal value not allowed.'
          smsLoading.value = false
          return
        }
      }

      const rawCampaign = localStorage.getItem('campaign')
      const campaign = rawCampaign ? JSON.parse(rawCampaign) : null
      const campaignId = campaign?.id
      const uniqueId = form.value.uniqueId.value
      const frequency = freqOption?.name
      const amount = resolveDonationAmount({
        customAmount,
        donationAmount: form.value.donationAmount.value,
        defaultAmount: null,
      })
      const donorName = donorData.donor.firstName + ' ' + donorData.donor.lastName
      // This token only proves who is asking for the text. The grant the donor will use
      // days later is minted by Django, which also appends the fundraiser's name.
      const token = readStoredFormToken() || (await requestDonorFormToken(campaignId))
      const url = `${uniqueId}/verification?campaign=${campaignId}&d_amount=${amount}&d_frequency=${frequency}&d_email=${props.email}`

      try {
        const response = await DonationApi.csc.smsDonorForDonationVerification({
          mobilePhone: props.mobilePhone,
          url: url,
          uniqueId: uniqueId,
          donorName: donorName,
          formToken: token,
        })

        smsSent.value = true
        form.value.donorVerificationSms.value = true
      } catch (error) {
        smsError.value = 'Failed to send SMS. Please try again later.'
        console.error('Error Sending SMS:', error)
      } finally {
        smsLoading.value = false
      }
    }

    watch(
      () => form.value.cardNumber.value,
      async (newVal) => {
        // For the Blackbaud Checkout workflows the card never lives in this form,
        // so we don't run BIN checks here.
        if (tokenizesCardOffForm.value) return
        if (typeof newVal !== 'string' || !newVal) return

        const digits = newVal.replace(/\D/g, '')

        if (digits.length >= 16 || digits.length >= 15) {
          const bin = digits.slice(0, 6)
          const isAvailable = await checkBin(bin)

          if (isAvailable === false) {
            form.value.cardNumber.value = ''
            form.value.cardNumber.errors = [
              {
                message: 'This type of card cannot be processed. Please use a different one.',
                code: 'invalid_bin',
              },
            ]
          } else {
            form.value.cardNumber.errors = []
          }
        }
      },
    )

    // Blackbaud's inline button begins authorization the instant it is clicked, so the
    // rest of the form has to be provably valid before we let it become clickable. We
    // validate a throwaway copy of the form to avoid painting errors on fields the donor
    // has not reached yet.
    const hostFormValid = computed(() => {
      // Mirror the trimming that validatePaymentForm() applies, so a field holding only
      // whitespace can't enable the button and then fail on submit.
      const values = { ...form.value.value }
      Object.keys(values).forEach((key) => {
        if (typeof values[key] === 'string') {
          values[key] = values[key].trim().replace(/\s+/g, ' ')
        }
      })

      const probe = PaymentDonorForm.create(values)
      stripCardValidators(probe)
      probe.validate()
      return probe.isValid
    })

    const inlineAmount = computed(() => {
      const freq = form.value.levelAutorepeat.value
      if (!freq) return 0
      const amount = resolveDonationAmount({
        customAmount: customAmountInput.value,
        donationAmount: form.value.donationAmount.value,
        defaultAmount: freq.defaultAmount ?? null,
      })
      if (!amount) return 0
      if (freq.maximum && amount > freq.maximum) return 0
      return amount
    })

    const inlineSubmitEnabled = computed(
      () =>
        inlineCheckoutValid.value &&
        hostFormValid.value &&
        Boolean(inlineAmount.value) &&
        !customAmountErrors.value.length,
    )

    const syncInlineBillingName = () => {
      const name = `${form.value.billingNameFirst.value || ''} ${
        form.value.billingNameLast.value || ''
      }`.trim()
      inlineCheckout.value?.setBillingName(name)
    }

    const teardownInlineCheckout = () => {
      inlineCheckout.value?.destroy()
      inlineCheckout.value = null
      inlineCheckoutReady.value = false
      inlineCheckoutValid.value = false
      inlineCheckoutBusy.value = false
    }

    const setupInlineCheckout = async () => {
      if (!isInlineCheckout.value || inlineCheckout.value) return
      const config = props.checkoutConfig || {}
      inlineCheckoutError.value = ''

      const controller = createInlineCheckout({
        environmentId: config.environmentId,
        paymentConfigurationId: config.paymentConfigurationId,
        zone: config.checkoutZone,
        onReady: (ready) => {
          inlineCheckoutReady.value = ready
        },
        onValidChange: (valid) => {
          inlineCheckoutValid.value = valid
        },
        onLoadingChange: (busy) => {
          inlineCheckoutBusy.value = busy
        },
        onAuthorizationChange: (inProgress) => {
          inlineCheckoutBusy.value = inProgress
        },
        onClicked: () => {
          // Blackbaud emits this on every click, enabled or not, which makes it our only
          // chance to tell the donor why a disabled button did nothing.
          if (inlineSubmitEnabled.value) {
            emit('checkoutStarted', buildSubmitPayload())
            return
          }
          validatePaymentForm()
        },
        onToken: (transactionToken) => {
          inlineCheckoutError.value = ''
          emit('submitForm', buildSubmitPayload({ transactionToken }))
          form.value.uniqueId.value = generateUUID()
        },
        onError: (message) => {
          inlineCheckoutBusy.value = false
          inlineCheckoutError.value = message
          emit('checkoutError', message)
        },
      })

      inlineCheckout.value = controller

      try {
        await controller.mount({
          inlineElementId: 'bb-inline-checkout',
          buttonElementId: 'bb-complete-checkout-button',
          buttonName: 'bb-form-submit-button',
        })
        controller.setEnabled(inlineSubmitEnabled.value)
        controller.setAmount(inlineAmount.value)
        syncInlineBillingName()
      } catch (e) {
        console.error('Blackbaud inline checkout failed to load:', e)
        teardownInlineCheckout()
        inlineCheckoutError.value =
          e?.message || 'Could not load the secure payment fields. Please refresh and try again.'
      }
    }

    watch(
      () => props.isPaymentStepVisible && isInlineCheckout.value,
      (shouldMount) => {
        if (shouldMount) nextTick(setupInlineCheckout)
      },
      { immediate: true },
    )

    // Fetch the reCAPTCHA library while the donor fills in the form. The token itself is
    // requested at submit time because it expires within a couple of minutes.
    watch(
      () => props.isPaymentStepVisible && props.checkoutConfig?.recaptchaSiteKey,
      (siteKey) => {
        if (!siteKey) return
        loadRecaptcha(siteKey).catch((e) => {
          console.error('Could not preload Google reCAPTCHA:', e)
        })
      },
      { immediate: true },
    )

    watch(inlineSubmitEnabled, (enabled) => inlineCheckout.value?.setEnabled(enabled))
    watch(inlineAmount, (amount) => inlineCheckout.value?.setAmount(amount))
    watch(
      () => [form.value.billingNameFirst.value, form.value.billingNameLast.value],
      syncInlineBillingName,
    )
    watch(
      () => form.value.levelAutorepeat.value,
      (freq) => inlineCheckout.value?.setRecurring(Boolean(freq?.value) && freq.value !== ONETIME),
    )

    onBeforeUnmount(teardownInlineCheckout)

    return {
      form,
      donateSuccess,
      amountRadioButtons,
      frequency,
      selectedFrequency,
      amount,
      selectedAmount,
      minCustomAmount,
      terms,
      submitPaymentDataForm,
      clearCustomAmount,
      prevStep,
      DonorFormCustomQuestions,
      questionData,
      autoSetAddress,
      usStateData,
      maskCreditCard,
      unmaskCreditCard,
      maskCCVisible,
      maskCCLength,
      hiddenCreditCard,
      checkCustomAmount,
      customAmountErrors,
      cardBrand,
      isCustomAmountFocused,
      customAmountInput,
      customAmountInputSize,
      sortedSuggestedAmounts,
      onCustomAmountFocus,
      onCustomAmountInputEvent,
      onCustomAmountBlur,
      paymentProvider,
      isInlineCheckout,
      tokenizesCardOffForm,
      inlineCheckoutReady,
      inlineCheckoutBusy,
      inlineCheckoutError,
      handleFrequencyChange,
      billingAddressAutofilled,
      handleBillingAddressInput,
      handleBillingAddressBlur,
      onBillingAddressPlaceSelected,
      onBillingAddressInvalidSelection,
      // Unique frequency options
      uniqueFrequencyOptions,
      isYearlySelected,
      smsForDonationVerification,
      smsSent,
      smsError,
      smsLoading,
      canShowSMSButton,
      qrCode,
      showBillingLastNameMismatchWarning,
      evaluateBillingLastNameMismatchOnBlur,
      onBillingNameInput,
    }
  },
  methods: {
    getQuestionData(data) {
      this.questionData = data
    },
  },
}
</script>
